import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { BiSave, BiX, BiUser, BiBriefcase, BiFile, BiPlus, BiTrash, BiBullseye, BiDownload } from 'react-icons/bi';
import { toast } from "react-toastify";
import { API_BASE_URL } from '../utils/urls.js';
import axiosClient from '../utils/apiClient.js';
import Select from "react-select";
// --- Static Data Definitions (Simplified for PI Case)
// import Select from "react-select"; ---
const PI_STATUSES = ['Open', 'Researching', 'Active Surveillance', 'Report Pending', 'Closed'];
const PREFERRED_CONTACT_METHODS = ['Email', 'Phone', 'Portal Notification'];

// 1. ADD NEW STATIC DATA DEFINITION FOR CASE TYPES
const PI_CASE_TYPES = [
    'Worker\'s Compensation',
    'Personal Injury',
    'Background Check',
    'Infidelity/Domestic',
    'Skip Trace/Locate',
    'Process Service',
    'Other'
];

// Define allowed file types for broad PI use
const ALLOWED_SUBJECT_FILE_TYPES = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp3,.mp4,.mov,.avi,.wav,.xls,.xlsx,.csv";

// --- Component Helper - Moved outside to prevent re-renders ---
const FormSection = ({ title, icon, children }) => (
    <div className="border border-gray-200 rounded-xl p-6 bg-white">
        <h2 className="text-xl font-extrabold text-blue-800 mb-4 flex items-center space-x-2 border-b pb-2">
            {icon}
            <span>{title}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {children}
        </div>
    </div>
);



// --- Component for Displaying Existing Case Files ---
// (No changes needed in this component, as it was not throwing errors)
const ExistingCaseFiles = ({ files, handleRemoveExistingFile }) => {
    const existingFiles = files.filter(f => !f.isNew);

    if (existingFiles.length === 0) return null;

    return (
        <div className="col-span-full pt-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">Existing Case Files ({existingFiles.length}):</label>
            <ul className="text-sm text-gray-700 space-y-2">
                {existingFiles.map((item) => {
                    const deleteType = item.type || (item.file_type?.includes('image') || item.file_type?.includes('video') ? 'media' : 'document');

                    return (
                        <li key={item.id} className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200">
                            <div className="truncate pr-4 flex items-center">
                                <span className="font-semibold text-blue-800 mr-2">{item.title}</span>
                                <span className="text-gray-500 text-xs">({item.file_name} - {deleteType.toUpperCase()})</span>
                                <a href={`${API_BASE_URL}/files/download?path=${item.file_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 ml-3">
                                    <BiDownload className="w-4 h-4" />
                                </a>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemoveExistingFile(item.id, deleteType)}
                                className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                            >
                                <BiTrash className="w-5 h-5" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};


// --- Subject File Management Block (handleTitleChangeTemp and handleFileUploadTemp fixes) ---
const SubjectFileUploader = ({ subject, index, handleSubjectAddFile, handleSubjectRemoveFile }) => {
    // Local state for the single file upload flow within the subject block
    const [currentSubjectFileUpload, setCurrentSubjectFileUpload] = useState({ file: null, title: '' });

    const handleFileUploadTemp = (e) => { // <-- DEFINED HERE
        const file = e.target.files[0];
        if (file) {
            setCurrentSubjectFileUpload(prev => ({ ...prev, file: file, title: prev.title || file.name }));
        }
    };

    const handleTitleChangeTemp = (e) => { // <-- DEFINED HERE
        setCurrentSubjectFileUpload(prev => ({ ...prev, title: e.target.value }));
    };

    const addFile = () => {
        if (currentSubjectFileUpload.file && currentSubjectFileUpload.title.trim() !== '') {
            handleSubjectAddFile(index, {
                file: currentSubjectFileUpload.file,
                title: currentSubjectFileUpload.title,
                isNew: true, // Mark this as a new file for upload
            });
            setCurrentSubjectFileUpload({ file: null, title: '' });
            document.getElementById(`subject_file_upload_${index}`).value = null; // Reset file input
        } else {
            alert('Please select a file and enter a title for the subject document.');
        }
    };

    return (
        <div className="space-y-4 border border-blue-100 p-4 rounded-lg bg-white mt-4">
            <h4 className="text-md font-bold text-gray-700">Subject Attachments</h4>
            {/* Display Subject Uploaded Files (Existing & New) */}
            {subject.subject_files && subject.subject_files.length > 0 && (
                <div className="col-span-full pt-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1">Files for {subject.full_name || `Subject ${index + 1}`} ({subject.subject_files.length}):</label>
                    <ul className="text-xs text-gray-700 space-y-1">
                        {subject.subject_files.map((item, fileIndex) => (
                            <li key={item.id || fileIndex} className="flex justify-between items-center bg-gray-100 p-2 rounded-md border border-gray-200">
                                <div className="truncate pr-4 flex items-center">
                                    <span className={`font-semibold ${item.isNew ? 'text-green-700' : 'text-blue-800'}`}>{item.title}</span>
                                    <span className="text-gray-500 ml-2">({item.file_name || item.file?.name})</span>
                                    <span className={`ml-2 text-xs font-bold ${item.isNew ? 'text-green-500' : 'text-gray-500'}`}>
                                        {item.isNew ? ' (NEW - Upload Pending)' : ' (Existing)'}
                                    </span>
                                    {!item.isNew && (
                                        <a href={`${API_BASE_URL}/files/download?path=${item.file_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 ml-3">
                                            <BiDownload className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                                {/* Use item.id for existing files, otherwise fileIndex for new local files */}
                                <button type="button" onClick={() => handleSubjectRemoveFile(index, fileIndex, item.id)} className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2">
                                    <BiTrash className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* New File Upload Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4 border-gray-200">
                <div className="md:col-span-2">
                    <label htmlFor={`subject_file_title_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Attachment Title</label>
                    <input
                        type="text"
                        id={`subject_file_title_${index}`}
                        name="subject_file_title"
                        placeholder="e.g., Surveillance Photo, Background Check Report"
                        value={currentSubjectFileUpload.title}
                        onChange={handleTitleChangeTemp} // <-- USES DEFINED HANDLER
                        className="p-1.5 border rounded-md w-full"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">File Upload</label>
                    <input
                        type="file"
                        id={`subject_file_upload_${index}`}
                        onChange={handleFileUploadTemp} // <-- USES DEFINED HANDLER
                        accept={ALLOWED_SUBJECT_FILE_TYPES}
                        className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer p-1.5"
                    />
                </div>
            </div>

            <button
                type="button"
                onClick={addFile}
                disabled={!currentSubjectFileUpload.file || currentSubjectFileUpload.title.trim() === ''}
                className="flex items-center px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 transition duration-300 disabled:opacity-50"
            >
                <BiPlus className="w-4 h-4 mr-1" /> Add Subject File
            </button>
        </div>
    );
}

// --- Subject Form Block (handleSubjectChange and handleRemoveSubject fixes) ---
const SubjectFormBlock = ({ subject, index, handleSubjectChange, handleRemoveSubject, canRemove, handleSubjectAddFile, handleSubjectRemoveFile }) => {
    return (
        <div className="col-span-full border border-gray-300 p-4 rounded-lg space-y-4 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-700">Subject #{index + 1}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Basic Info */}
                <div>
                    <label htmlFor={`full_name_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Full Name of Subject *</label>
                    <input type="text" id={`full_name_${index}`} name="full_name" value={subject.full_name || ''} onChange={(e) => handleSubjectChange(index, e)} required className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`aliases_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Aliases / AKA</label>
                    <input type="text" id={`aliases_${index}`} name="aliases" value={subject.aliases || ''} onChange={(e) => handleSubjectChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>

                {/* Descriptive Info */}
                <div className="col-span-full">
                    <label htmlFor={`physical_description_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Physical Description</label>
                    <textarea id={`physical_description_${index}`} name="physical_description" placeholder="Gender, Race, Height, Hair, Tattoos, distinguishing marks, etc." value={subject.physical_description || ''} onChange={(e) => handleSubjectChange(index, e)} rows="3" className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`last_seen_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Last Seen/Known Location</label>
                    <input type="text" id={`last_seen_${index}`} name="last_seen" value={subject.last_seen || ''} onChange={(e) => handleSubjectChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
                <div className="col-span-full">
                    <label htmlFor={`notes_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Subject Notes</label>
                    <textarea id={`notes_${index}`} name="notes" placeholder="Any known habits, hangouts, or warnings." value={subject.notes || ''} onChange={(e) => handleSubjectChange(index, e)} rows="2" className="p-1.5 border rounded-md w-full" />
                </div>
            </div>

            {/* Subject File Uploader Component */}
            <SubjectFileUploader
                subject={subject}
                index={index}
                handleSubjectAddFile={handleSubjectAddFile}
                handleSubjectRemoveFile={handleSubjectRemoveFile}
            />

            {canRemove && (
                <button type="button" onClick={() => handleRemoveSubject(index)} className="flex items-center text-red-500 hover:text-red-700 text-sm mt-4">
                    <BiTrash className="w-4 h-4 mr-1" /> Remove Subject
                </button>
            )}
        </div>
    );
};

const CaseFormPi = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();

    // The new PI API base URL
    const PI_API_URL = `${API_BASE_URL}/pi-cases`;

    // Subject Template with new 'subject_files' array
    const newSubjectTemplate = {
        full_name: '', aliases: '', primary_address: '', apartment_unit: '', city_state_zip: '', alternate_address: '',
        phone: '', email: '', physical_description: '', vehicle_description: '', notes: '', last_seen: '',
        status: 'Active', subject_files: [], // Array to hold { file: File, title: string, isNew: bool, id: optional }
    };

    // 2. Update Core Form State to include case_type
    const [formData, setFormData] = useState({
        case_number: '',
        case_name: '',
        case_type: 'Worker\'s Compensation', // <--- INITIALIZED HERE WITH A DEFAULT VALUE
        status: 'Open', client_id: '', description: '',
        assigned_to_ids: [], // Changed to array for multiple select
        due_date: '', is_pi: true, objectives: '', relevant_dates: '', known_locations: '', client_reference_number: '',
        client_contact_person: '', client_phone_number: '', client_email_address: '', client_billing_address: '',
        preferred_contact_method: 'Email', custom_fields: {},
    });

    // 2. Nested Data States
    const [subjectData, setSubjectData] = useState([newSubjectTemplate]);

    // Files array now holds both new files (file: File, isNew: true) and existing files (id: number, isNew: false)
    const [caseFiles, setCaseFiles] = useState([]);
    const [currentFileUpload, setCurrentFileUpload] = useState({ file: null, title: '' });

    // 3. Lookup Data & UI States (Same as before)
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitStatus, setSubmitStatus] = useState('');

    const caseStatuses = PI_STATUSES;

    const formatRoleName = (roleName = "") => {
        return roleName.replace(/_/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
    };

    // --- API Handlers ---
    const token = localStorage.getItem('token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };


    useEffect(() => {
        const fetchCaseLimitStatus = async () => {
            try {
                const { data } = await axiosClient.get('/subs/case-limit');

                if (data.subscriptionExpired) {
                    alert("Your subscription has expired. Please renew to create, edit, or assign agents to cases.");
                    return navigate('/cases/pi');
                }

                if (!data.canCreateMoreCases && !isEditMode) {
                    alert("Your plan case limit is reached.\nDelete any case to create a new one.");
                    setTimeout(() => {
                        navigate('/cases/pi');
                    }, 100); // Slight delay ensures alert renders
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchCaseLimitStatus();
    }, [navigate]);

    const handleDeleteCaseFile = async (fileId, fileType) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this ${fileType} file? This action is permanent.`);
        if (!confirmDelete) return;

        try {
            // Delete the file via the new API endpoint
            await axios.delete(`${PI_API_URL}/files/${fileType}/${fileId}`, authHeaders);

            // Remove the file from local state
            setCaseFiles(prev => prev.filter(f => f.id !== fileId));

            alert(`${fileType.toUpperCase()} deleted successfully!`);
        } catch (err) {
            console.error('Error deleting file:', err);
            alert('Failed to delete file. Check console for details.');
        }
    }

    const handleDeleteSubjectFile = async (fileId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this subject attachment? This action is permanent.');
        if (!confirmDelete) return;

        try {
            // Delete the subject file via the new API endpoint
            await axios.delete(`${PI_API_URL}/subject-files/${fileId}`, authHeaders);

            // Remove the file from local state
            setSubjectData(prev => prev.map(subject => ({
                ...subject,
                subject_files: subject.subject_files.filter(f => f.id !== fileId)
            })));

            alert('Subject Attachment deleted successfully!');
        } catch (err) {
            console.error('Error deleting subject file:', err);
            alert('Failed to delete subject attachment. Check console for details.');
        }
    }


    // --- Data Fetching ---
    useEffect(() => {
        const fetchData = async () => {
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }

            try {
                const [usersRes, clientsRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/users/assignable/agents`, authHeaders),
                    axios.get(`${API_BASE_URL}/clients/all`, authHeaders),
                ]);

                console.log("Got Internal Users", usersRes)
                // Filter users to include only those with agent/server/manager roles for assignment
                setUsers(usersRes.data);
                setClients(clientsRes.data.data);

                if (isEditMode) {
                    const caseRes = await axios.get(`${PI_API_URL}/${id}`, authHeaders);
                    const data = caseRes.data;
                    console.log("Case Data", data)

                    // --- Case Files & Media ---
                    const existingDocs = (data.documents || []).map(d => ({ ...d, isNew: false, type: 'document' }));
                    const existingMedia = (data.media || []).map(m => ({ ...m, isNew: false, type: 'media' }));
                    setCaseFiles([...existingDocs, ...existingMedia]);

                    // --- Subject Data ---
                    let subjectsWithFiles = (data.subjects || []).map(s => {
                        const existingSubjectFiles = (s.subject_attachments || []).map(f => ({
                            ...f,
                            isNew: false,
                            file_name: f.file_name
                        }));

                        return {
                            ...s,
                            subject_files: existingSubjectFiles
                        };
                    });

                    setSubjectData(subjectsWithFiles.length > 0 ? subjectsWithFiles : [newSubjectTemplate]);

                    let assignedIds = [];
                    if (data.assigned_to_ids) {
                        if (Array.isArray(data.assigned_to_ids)) {
                            // Case: API already returned an array of IDs (ideal scenario)
                            assignedIds = data.assigned_to_ids;
                        } else if (typeof data.assigned_to_ids === 'string') {
                            // Case: API returned a comma-separated string (your current assumption)
                            assignedIds = data.assigned_to_ids
                                .split(',')
                                .map(idStr => parseInt(idStr.trim(), 10))
                                .filter(id => !isNaN(id)); // Filter out any non-numeric results
                        } else if (typeof data.assigned_to_ids === 'number') {
                            // Case: API returned a single number (for legacy/single assignee)
                            assignedIds = [data.assigned_to_ids];
                        }
                    }


                    // --- Main Form Data ---
                    setFormData(prev => ({
                        ...prev, ...data,
                        due_date: data.due_date ? data.due_date.split('T')[0] : '',
                        custom_fields: data.custom_fields || {},
                        assigned_to_ids: assignedIds,
                        client_contact_person: data.client_info?.contact_person || '', client_phone_number: data.client_info?.phone_number || '',
                        client_email_address: data.client_info?.email_address || '', client_billing_address: data.client_info?.billing_address || '',
                        preferred_contact_method: data.client_info?.preferred_contact_method || 'Email',
                        is_pi: data.is_pi === true,
                        // Ensure case_type loads correctly, defaulting to the one in initial state if null
                        case_type: data.case_type || 'Worker\'s Compensation', // <--- LOADED HERE
                    }));
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.response?.data?.message || 'Failed to load PI case data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, isEditMode, PI_API_URL, token]);


    // --- Handlers (Modified for Multiple Assignees and File Deletion) ---
    const handleChange = useCallback((e) => {
        const { name, value, type, options } = e.target;

        if (name === 'assigned_to_ids' && type === 'select-multiple') {
            const selectedIds = Array.from(options)
                .filter(option => option.selected)
                .map(option => parseInt(option.value, 10));

            setFormData(prev => ({ ...prev, [name]: selectedIds }));
            return;
        }

        // Existing logic for non-array fields
        setFormData(prev => {
            let newState = { ...prev, [name]: value };
            if (name === 'client_id') {
                const selectedClient = clients.find(c => c.id === parseInt(value, 10));
                if (selectedClient) {
                    newState = {
                        ...newState,
                        client_contact_person: selectedClient.contact_person || `${selectedClient.first_name} ${selectedClient.last_name}`,
                        client_phone_number: selectedClient.phone_number || '',
                        client_email_address: selectedClient.email || '',
                        client_billing_address: selectedClient.billing_address || '',
                        preferred_contact_method: selectedClient.preferred_contact_method || 'Email',
                    };
                } else if (value === '') {
                    newState = {
                        ...newState,
                        client_contact_person: '', client_phone_number: '', client_email_address: '',
                        client_billing_address: '', preferred_contact_method: 'Email',
                    };
                }
            }
            return newState;
        });
    }, [clients]);

    // Define handleCustomFieldChange (FIXED ERROR Line 787)
    const handleCustomFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            custom_fields: { ...prev.custom_fields, [name]: value },
        }));
    };

    // Define handleSubjectChange (FIXED ERROR Line 676)
    const handleSubjectChange = (index, e) => {
        const { name, value } = e.target;
        const newSubjects = [...subjectData];
        newSubjects[index][name] = value;
        setSubjectData(newSubjects);
    };

    // Define handleRemoveSubject (FIXED ERROR Line 677)
    const handleRemoveSubject = (indexToRemove) => {
        setSubjectData(prev => prev.filter((_, i) => i !== indexToRemove));
    };


    // --- Subject File Handlers ---
    const handleSubjectAddFile = (index, fileObject) => {
        setSubjectData(prev => prev.map((subject, i) =>
            i === index ? { ...subject, subject_files: [...subject.subject_files, fileObject] } : subject
        ));
    };

    const handleSubjectRemoveFile = (subjectIndex, fileIndex, fileId) => {
        const fileToRemove = subjectData[subjectIndex].subject_files[fileIndex];

        // If it's an existing file, call the API delete function
        if (fileId && !fileToRemove.isNew) {
            handleDeleteSubjectFile(fileId);
        }

        // Remove file from local state regardless (if new, it just won't be submitted)
        setSubjectData(prev => prev.map((subject, i) =>
            i === subjectIndex
                ? { ...subject, subject_files: subject.subject_files.filter((_, j) => j !== fileIndex) }
                : subject
        ));
    };


    // --- General Case File Handlers (Modified for Existing File Removal) ---
    const handleFileUploadTemp = (e) => { // <-- DEFINED HERE (FIXED ERROR Line 721)
        const file = e.target.files[0];
        if (file) {
            setCurrentFileUpload(prev => ({ ...prev, file: file, title: prev.title || file.name }));
        }
    };

    const handleTitleChangeTemp = (e) => { // <-- DEFINED HERE (FIXED ERROR Line 711)
        setCurrentFileUpload(prev => ({ ...prev, title: e.target.value }));
    };

    const handleAddFile = () => {
        if (currentFileUpload.file && currentFileUpload.title.trim() !== '') {
            setCaseFiles(prev => [...prev, {
                ...currentFileUpload,
                isNew: true, // Mark as new file for upload
                file_name: currentFileUpload.file.name,
                // The backend will determine if it's document/media based on file type
            }]);
            setCurrentFileUpload({ file: null, title: '' });
            document.getElementById('case_file_upload').value = null;
        } else {
            alert('Please select a file and enter a title.');
        }
    };

    // Handler for removing a NEWLY added file (only removes from local state)
    const handleRemoveNewFile = (indexToRemove) => {
        // Find the index of the file in the main caseFiles array that is NEW and matches the filter index
        const newFiles = caseFiles.filter(f => f.isNew);
        if (indexToRemove >= 0 && indexToRemove < newFiles.length) {
            const fileToRemove = newFiles[indexToRemove];

            // Remove the file from the main caseFiles array
            setCaseFiles(prev => {
                // Find the first matching file (by reference or other unique property if available)
                const globalIndex = prev.findIndex(f => f === fileToRemove);
                if (globalIndex > -1) {
                    return prev.filter((_, i) => i !== globalIndex);
                }
                return prev;
            });
        }
    };

    // Handler for removing an EXISTING file (removes from local state and calls API delete)
    const handleRemoveExistingFile = (fileId, fileType) => {
        handleDeleteCaseFile(fileId, fileType);
        // Local state update happens inside handleDeleteCaseFile for simplicity
    };


    // --- Submission (Updated for Multiple Agents and File Deletion) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('submitting');
        setError(null);

        // 1. Prepare Data Payload
        const clientInfoData = {
            contact_person: formData.client_contact_person, phone_number: formData.client_phone_number,
            email_address: formData.client_email_address, billing_address: formData.client_billing_address,
            preferred_contact_method: formData.preferred_contact_method,
        }

        // Join assigned_to_ids array into a comma-separated string for the assigned_to_id field
        const assignedToIdString = formData.assigned_to_ids.join(',');

        const submissionData = {
            case_data: {
                ...formData,
                client_id: formData.client_id || null,
                assigned_to_id: assignedToIdString || null, // SEND AS STRING
                due_date: formData.due_date || null,
                is_pi: true,
                client_info: clientInfoData,
            },
            // Filter out existing subject files for submission, only send subject data
            subject_data: subjectData.map(({ subject_files, ...rest }) => ({ ...rest, status: rest.status || 'Active' })),
            custom_fields: formData.custom_fields,
        };

        console.log("Assigned To", assignedToIdString)

        console.log("Subject Data", submissionData.subject_data)
        let subjectCreationResponse = [];
        let finalCaseId;
        try {
            // 2. Submit Case Data (Create/Update)
            let response;
            if (isEditMode) {
                const updatePayload = {
                    ...submissionData.case_data,
                    custom_fields: submissionData.custom_fields,
                    subject_data: submissionData.subject_data,
                };
                response = await axios.put(`${PI_API_URL}/${id}`, updatePayload, authHeaders);
                finalCaseId = id;
            } else {
                response = await axios.post(PI_API_URL, submissionData, authHeaders);
                finalCaseId = response.data.caseId;
                subjectCreationResponse = response.data.subjects || [];
                // 🔥 FIX: Merge returned subject IDs into subjectData
                if (!isEditMode && subjectCreationResponse.length > 0) {
                    setSubjectData(prevSubjects =>
                        prevSubjects.map((s, index) => ({
                            ...s,
                            id: subjectCreationResponse[index]?.id   // attach backend ID
                        }))
                    );

                    // Also update local subjectData variable used below
                    subjectData.forEach((s, i) => {
                        s.id = subjectCreationResponse[i]?.id;
                    });
                }
            }

            // 3. Upload NEW Files
            const uploadPromises = [];
            const uploadConfig = { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } };

            // A. Upload general case files (only those marked as new)
            caseFiles.filter(item => item.isNew).forEach(item => {
                const fd = new FormData();
                fd.append('document', item.file);
                fd.append('document_type', item.title);
                uploadPromises.push(axios.post(`${PI_API_URL}/${finalCaseId}/upload`, fd, uploadConfig));
            });

            const updatedSubjects = isEditMode ? subjectData : subjectCreationResponse.map((s, i) => ({
                ...subjectData[i],
                id: s.id
            }));

            // B. Upload subject attachments (only those marked as new)
            // Note: Subject ID is only available after a subject is created on the backend.
            // If this is a new case (not edit mode), subject IDs might not be available here,
            // which requires a more complex solution (e.g., waiting for subject creation responses).
            // ASSUMPTION: Subject IDs are available either from edit mode or immediately after case creation/subject insert.
            updatedSubjects.forEach((subject) => {
                const subjectId = subject.id; // Subject ID is required for the dedicated subject-file route
                if (!subjectId) {
                    console.warn(`Subject ID missing for file upload on subject: ${subject.full_name}. Skipping file uploads for this subject.`);
                    return;
                }

                subject.subject_files.filter(item => item.isNew).forEach(item => {
                    const fd = new FormData();
                    fd.append('document', item.file);
                    fd.append('document_type', item.title);
                    fd.append('subject_id', subjectId); // Pass the subject ID

                    // Use the dedicated subject-file upload route
                    uploadPromises.push(axios.post(`${PI_API_URL}/${finalCaseId}/upload/subject-file`, fd, uploadConfig));
                })
            });

            await Promise.all(uploadPromises);

            toast.success(`Case ${isEditMode ? 'updated' : 'created'} successfully!`);

            setSubmitStatus('success');
            setTimeout(() => navigate('/cases/pi'), 1000); // Redirect to the main case list
        } catch (err) {
            setSubmitStatus('error');
            toast.error('Failed to save the case.');
            console.error('Submission Error:', err);
            setError(err.response?.data?.message || 'Submission failed. Please try again.');
        }
    };

    if (loading) return <div className="p-6 text-center">Loading form...</div>;
    if (error && submitStatus !== 'submitting') return <div className="p-6 text-center text-red-500">Error: {error}</div>;

    return (
        <div className="p-1 sm:p-4 lg:p-6 max-w-6xl mx-auto">
            <h1 className="text-4xl font-extrabold text-gray-800 mb-8">{id ? 'Edit Private Investigation Case' : 'IntelServe™ Private Investigation Intake'}</h1>
            <form onSubmit={handleSubmit} className="space-y-8">


                {/* 2. Client Information */}
                <FormSection title="1. Client Information" icon={<BiUser className="w-6 h-6" />}>

                    <div className="col-span-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search & Select Client / Firm *
                        </label>

                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            {/* Select */}
                            <div className="flex-1">
                                <Select
                                    options={clients.map((c) => ({
                                        value: c.id,
                                        label: c.agency
                                            ? `${c.agency}${c.first_name && ' ('} ${c.first_name} ${c.last_name == 'NA' ? "" : c.last_name}${c.first_name && ')'}`
                                            : `${c.first_name} ${c.last_name == 'NA' ? "" : c.last_name}`,
                                        client: c,
                                    }))}
                                    value={
                                        formData.client_id
                                            ? clients
                                                .map((c) => ({
                                                    value: c.id,
                                                    label: c.agency
                                                        ? `${c.agency}${c.first_name && ' ('} ${c.first_name} ${c.last_name == 'NA' ? "" : c.last_name}${c.first_name && ')'}`
                                                        : `${c.first_name} ${c.last_name == 'NA' ? "" : c.last_name}`,
                                                    client: c,
                                                }))
                                                .find((opt) => opt.value === formData.client_id)
                                            : null
                                    }
                                    onChange={(selected) => {
                                        const selectedClient = selected.client;

                                        setFormData((prev) => ({
                                            ...prev,
                                            client_id: selected.value,
                                            client_contact_person:
                                                selectedClient.contact_person ||
                                                `${selectedClient.first_name} ${selectedClient.last_name}`,
                                            client_phone_number: selectedClient.phone_number || "",
                                            client_email_address: selectedClient.email || "",
                                            client_billing_address: selectedClient.billing_address || "",
                                            preferred_contact_method:
                                                selectedClient.preferred_contact_method || "Email",
                                        }));
                                    }}
                                    placeholder="Search client or agency..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isSearchable
                                />
                            </div>

                            {/* Buttons */}
                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                {/* Edit Button */}
                                {formData.client_id && (
                                    <Link
                                        to={`/clients/${formData.client_id}`}
                                        className="w-full sm:w-auto text-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                                    >
                                        Edit
                                    </Link>
                                )}

                                {/* Add Button */}
                                <Link
                                    to="/clients/new"
                                    className="w-full sm:w-auto text-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                                >
                                    Add New Client
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="client_contact_person" className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                        <input disabled={formData.client_id ? false : true} type="text" id="client_contact_person" name="client_contact_person" value={formData.client_contact_person} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="client_phone_number" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                        <input disabled={formData.client_id ? false : true} type="tel" id="client_phone_number" name="client_phone_number" value={formData.client_phone_number} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="client_email_address" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                        <input disabled={formData.client_id ? false : true} type="email" id="client_email_address" name="client_email_address" value={formData.client_email_address} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="client_billing_address" className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                        <input disabled={formData.client_id ? false : true} type="text" id="client_billing_address" name="client_billing_address" value={formData.client_billing_address} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>

                    <div>
                        <label htmlFor="preferred_contact_method" className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact Method *</label>
                        <select disabled={formData.client_id ? false : true} id="preferred_contact_method" name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleChange} required className="p-1.5 border rounded-md bg-gray-50 w-full">
                            <option value="">Select...</option>
                            {PREFERRED_CONTACT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                </FormSection>

                {/* 1. Investigation Details */}
                <FormSection title="2. Investigation Details" icon={<BiBullseye className="w-6 h-6" />}>
                    {/* NEW FIELD: case_type */}
                    <div>
                        <label htmlFor="case_type" className="block text-sm font-medium text-gray-700 mb-1">Case Type *</label>
                        <select
                            id="case_type"
                            name="case_type"
                            value={formData.case_type}
                            onChange={handleChange}
                            required
                            className="p-1.5 border rounded-md bg-gray-50 w-full"
                        >
                            <option value="">Select Case Type...</option>
                            {PI_CASE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                        </select>
                    </div>
                    {/* END NEW FIELD */}
                    <div>
                        <label htmlFor="case_name" className="block text-sm font-medium text-gray-700 mb-1">Case Title *</label>
                        <input type="text" id="case_name" name="case_name" placeholder="e.g. Surveillance: Doe v. Public" value={formData.case_name} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="case_number" className="block text-sm font-medium text-gray-700 mb-1">Case Number</label>
                        <input type="text" id="case_number" name="case_number" value={formData.case_number} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>

                    <div className="md:col-span-2">
                        <label htmlFor="objectives" className="block text-sm font-medium text-gray-700 mb-1">Investigation Objectives *</label>
                        <textarea id="objectives" name="objectives" placeholder="What is the primary goal? (e.g., Locate subject, Verify employment, Document activity)" value={formData.objectives} onChange={handleChange} required rows="3" className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="known_locations" className="block text-sm font-medium text-gray-700 mb-1">Known Locations of Interest</label>
                        <textarea id="known_locations" name="known_locations" placeholder="Addresses, hangouts, business locations." value={formData.known_locations} onChange={handleChange} rows="2" className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="relevant_dates" className="block text-sm font-medium text-gray-700 mb-1">Relevant Dates/Times</label>
                        <textarea id="relevant_dates" name="relevant_dates" placeholder="Court dates, activity windows, surveillance times." value={formData.relevant_dates} onChange={handleChange} rows="2" className="p-1.5 border rounded-md w-full" />
                    </div>

                    <div>
                        <label htmlFor="client_reference_number" className="block text-sm font-medium text-gray-700 mb-1">Client Reference #</label>
                        <input type="text" id="client_reference_number" name="client_reference_number" value={formData.client_reference_number} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select id="status" name="status" value={formData.status} onChange={handleChange} className="p-1.5 border rounded-md bg-gray-50 w-full">
                            {caseStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input type="date" id="due_date" name="due_date" value={formData.due_date} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div className="col-span-full">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Internal Case Description / Summary</label>
                        <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows="3" className="p-1.5 border rounded-md w-full" />
                    </div>
                </FormSection>

                {/* 3. Subject Information (Replaces Servee) */}
                <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-4">
                    <h2 className="text-xl font-extrabold text-blue-800 flex items-center space-x-2 border-b pb-2">
                        <BiUser className="w-6 h-6" />
                        <span>3. Investigation Subject(s) Information</span>
                    </h2>
                    <div className="space-y-6">
                        {subjectData.map((subject, index) => (
                            <SubjectFormBlock
                                key={subject.id || index} // Use existing ID if available, otherwise index
                                subject={subject}
                                index={index}
                                handleSubjectChange={handleSubjectChange} // <-- PASSED DOWN
                                handleRemoveSubject={handleRemoveSubject} // <-- PASSED DOWN
                                handleSubjectAddFile={handleSubjectAddFile}
                                handleSubjectRemoveFile={handleSubjectRemoveFile}
                                canRemove={subjectData.length > 1}
                            />
                        ))}
                    </div>
                    <button type="button" onClick={() => setSubjectData(prev => [...prev, newSubjectTemplate])}
                        className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg shadow-md hover:bg-green-600 transition duration-300">
                        <BiPlus className="w-5 h-5 mr-1" /> Add Another Subject
                    </button>
                </div>

                {/* 4. General Case Documents */}
                <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-6">
                    <h2 className="text-xl font-extrabold text-blue-800 flex items-center space-x-2 border-b pb-2">
                        <BiFile className="w-6 h-6" />
                        <span>4. General Case Documents & Media</span>
                    </h2>

                    {/* Display Existing Case Files (Documents & Media) */}
                    {isEditMode && <ExistingCaseFiles files={caseFiles} handleRemoveExistingFile={handleRemoveExistingFile} />}

                    {/* Upload Case Files - ONE BY ONE WITH TITLE */}
                    <div className="space-y-4 border border-blue-100 p-4 rounded-lg bg-blue-50">
                        <h3 className="text-lg font-bold text-blue-700">Upload Supporting Documents</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label htmlFor="file_title" className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                                <input
                                    type="text"
                                    id="file_title"
                                    name="file_title"
                                    placeholder="e.g., Client Contract, Initial Briefing"
                                    value={currentFileUpload.title}
                                    onChange={handleTitleChangeTemp} // <-- USES DEFINED HANDLER
                                    required={currentFileUpload.file !== null}
                                    className="p-1.5 border rounded-md w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">File Upload *</label>
                                <input
                                    type="file"
                                    id="case_file_upload"
                                    onChange={handleFileUploadTemp} // <-- USES DEFINED HANDLER
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp3,.mp4,.mov,.avi,.wav" // Broadened types
                                    className="block w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 cursor-pointer p-1.5"
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleAddFile}
                            disabled={!currentFileUpload.file || currentFileUpload.title.trim() === ''}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-300 disabled:opacity-50"
                        >
                            <BiPlus className="w-5 h-5 mr-1" /> Add File to Case
                        </button>
                    </div>

                    {/* Display NEW Files to be Uploaded */}
                    {caseFiles.filter(f => f.isNew).length > 0 && (
                        <div className="col-span-full">
                            <label className="block text-sm font-bold text-gray-700 mb-2">New Files to be Uploaded ({caseFiles.filter(f => f.isNew).length}):</label>
                            <ul className="text-sm text-gray-700 space-y-2">
                                {caseFiles.filter(f => f.isNew).map((item, index) => (
                                    <li key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200">
                                        <div className="truncate pr-4">
                                            <span className="font-semibold">{item.title}</span>
                                            <span className="text-gray-500 ml-2">({item.file.name})</span>
                                            <span className="text-xs text-gray-400 ml-2">({(item.file.size / 1024).toFixed(1)} KB)</span>
                                        </div>
                                        {/* Since this is a filtered array, index is correct for new file removal */}
                                        <button type="button" onClick={() => handleRemoveNewFile(index)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                                            <BiTrash className="w-5 h-5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* 5. Internal Assignments & Custom Fields */}
                <FormSection title="5. Internal Assignments" icon={<BiUser className="w-6 h-6" />}>
                    <div>
                        <label htmlFor="assigned_to_ids" className="block text-sm font-medium text-gray-700 mb-1">Assigned Agent(s) (Hold Ctrl/Cmd to select multiple)</label>
                        <select
                            id="assigned_to_ids"
                            name="assigned_to_ids"
                            multiple // Crucial for multi-select
                            value={formData.assigned_to_ids.map(String)} // Convert numbers to strings for select value
                            onChange={handleChange}
                            className="p-1.5 border rounded-md bg-gray-50 w-full h-40" // Increased height for better multi-select
                        >
                            <option value="">Unassigned</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>{user.first_name} {user.last_name} ({formatRoleName(user.role_name)})</option>
                            ))}
                        </select>
                    </div>

                    {/* Custom Fields - Keeping the example commented out as in original */}
                    {/* <div>
                        <label htmlFor="file_source" className="block text-sm font-medium text-gray-700 mb-1">Custom Field: File Source</label>
                        <input
                            type="text"
                            id="file_source"
                            name="file_source"
                            value={formData.custom_fields?.file_source || ''}
                            onChange={handleCustomFieldChange} // <-- USES DEFINED HANDLER
                            className="p-1.5 border rounded-md w-full"
                        />
                    </div> */}
                </FormSection>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/cases')}
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300"
                    >
                        <BiX className="inline-block w-5 h-5 mr-1" /> Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitStatus === 'submitting'}
                        className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition duration-300 disabled:opacity-50 flex items-center justify-center"
                    >
                        {submitStatus === 'submitting' ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving Case...
                            </>
                        ) : (
                            <>
                                <BiSave className="inline-block w-5 h-5 mr-1" /> {id ? 'Update Case' : 'Create New Intake'}
                            </>
                        )}
                    </button>
                </div>

                {submitStatus === 'success' && (
                    <p className="mt-4 text-green-500 text-center font-semibold">Case saved successfully! Redirecting...</p>
                )}
                {submitStatus === 'error' && (
                    <p className="mt-4 text-red-500 text-center font-semibold">{error}</p>
                )}
            </form>
        </div>
    );
};

export default CaseFormPi;